// Drafted copy carries links as Markdown, `[anchor](url)` (the document a rep
// dropped for the contact, see services/mailing_copy/attachment_link.py). Every
// provider receives them as HTML anchors; these helpers do the same for the rep
// who reads the copy on the board and pastes it into their own mailbox.
import { isHttpUrl } from "@/lib/safe-url"

const MARKDOWN_LINK_RE = /\[([^[\]\n]+)\]\((https?:\/\/[^\s()]+)\)/g

export type CopyPart =
  | { kind: "text"; text: string }
  | { kind: "link"; text: string; url: string }

export function splitMarkdownLinks(body: string): CopyPart[] {
  const parts: CopyPart[] = []
  let last = 0
  for (const match of body.matchAll(MARKDOWN_LINK_RE)) {
    const start = match.index ?? 0
    if (start > last)
      parts.push({ kind: "text", text: body.slice(last, start) })
    const [, text, url] = match
    parts.push(
      isHttpUrl(url) ? { kind: "link", text, url } : { kind: "text", text },
    )
    last = start + match[0].length
  }
  if (last < body.length) parts.push({ kind: "text", text: body.slice(last) })
  return parts
}

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
}

// The body as a mail client pastes it: anchors as links, line breaks kept.
export function markdownLinksToHtml(body: string): string {
  return splitMarkdownLinks(body)
    .map((part) =>
      part.kind === "link"
        ? `<a href="${escapeHtml(part.url)}">${escapeHtml(part.text)}</a>`
        : escapeHtml(part.text),
    )
    .join("")
    .replaceAll("\n", "<br>")
}

// The body for a plain-text paste: the address follows its anchor.
export function markdownLinksToPlain(body: string): string {
  return splitMarkdownLinks(body)
    .map((part) =>
      part.kind === "link" ? `${part.text} (${part.url})` : part.text,
    )
    .join("")
}
